/**
 * Utility functions for formatting data
 */

/**
 * Formats duration in seconds to MM:SS format
 * @param seconds - Duration in seconds (can be null)
 * @returns Formatted string (e.g., "2:45") or "-" if no duration
 */
export const formatDuration = (seconds: number | null): string => {
  if (!seconds || seconds === 0) return '-';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Formats date time string to localized short format
 * @param dateTimeString - ISO date time string
 * @returns Formatted string (e.g., "Jan 15, 2:30 PM") or original string if parsing fails
 */
export const formatDateTime = (dateTimeString: string): string => {
  try {
    const date = new Date(dateTimeString);
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateTimeString;
  }
};

/**
 * Formats phone number to (XXX) XXX-XXXX format
 * @param phone - Phone number string (can include +1 prefix and special characters)
 * @returns Formatted phone number (e.g., "(555) 123-4567") or original string if format doesn't match
 */
export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX for US numbers
  if (digits.length === 11 && digits.startsWith('1')) {
    // Remove leading 1
    const number = digits.slice(1);
    return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
  } else if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Return as-is if format doesn't match
  return phone;
};

/**
 * Formats date to relative time string
 * @param dateString - ISO date time string
 * @returns Relative time string (e.g., "Just now", "5m ago", "2h ago", "3d ago")
 */
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};
